import React from 'react';
import { useFadeIn } from '../hooks/useFadeIn';

const SERVICES = [
  {
    title: 'Greenhouse Cultivation',
    desc: 'Controlled-environment farming with precise management of temperature, humidity, and nutrients for year-round production.',
    icon: (
      <path d="M17 8C8 10 5.9 16.17 3.82 19.54A1 1 0 005.2 20.9C6.24 19.29 7.71 18 9 18c1.5 0 3 1 5 1 5 0 6-4 6-6V8c-.6.2-2 0-3 0z"/>
    ),
  }, {
    title: 'Greenhouse Construction & Management',
    desc: 'Whether you’re a commercial grower or a dedicated hobbyist, the right structure is the backbone of your success. We specialize in the design, engineering, and operational management of high-performance controlled environment agriculture (CEA) systems.',
    icon: (
      <path d="M17 8C8 10 5.9 16.17 3.82 19.54A1 1 0 005.2 20.9C6.24 19.29 7.71 18 9 18c1.5 0 3 1 5 1 5 0 6-4 6-6V8c-.6.2-2 0-3 0z"/>
    ),
  },
  {
    title: 'Open-Field Farming',
    desc: 'Responsible open-field cultivation leveraging natural resources while maintaining sustainable agricultural practices and soil health.',
    icon: (
      <>
        <path d="M12 2a7 7 0 000 14h.5A7.5 7.5 0 0020 9a7 7 0 00-7-7h-1zm0 2h1a5 5 0 015 5 5.5 5.5 0 01-5.5 5.5H12A5 5 0 017 9a5 5 0 015-5z"/>
        <path d="M11 14v8M8 17l3 2 3-2"/>
      </>
    ),
  },
  {
    title: 'Quality Assurance',
    desc: 'Every product meets rigorous local and international standards for safety, freshness, and nutritional quality.',
    icon: (
      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
    ),
  },
  {
    title: 'Sustainable Irrigation',
    desc: 'Efficient water management systems reducing consumption by up to 70% while maintaining optimal crop hydration.',
    icon: (
      <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z"/>
    ),
  },
  {
    title: 'Crop Protection',
    desc: 'Advanced climate-controlled systems protect crops from weather variability, pests, and environmental stressors.',
    icon: (
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    ),
  },
  {
    title: 'Agricultural Innovation',
    desc: 'Continuously adopting cutting-edge technology and research to maximize productivity and improve food quality.',
    icon: (
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
    ),
  },
];

export default function Services({ dark }) {
  const ref = useFadeIn();

  return (
    <section id="services" className={`py-24 px-6 ${dark ? 'bg-[#0d1a0f]' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto" ref={ref}>
        <div className="text-center mb-14 fade-in">
          <span className={`inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5
            ${dark ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-800'}`}>
            What We Do
          </span>
          <h2 className={`font-serif text-3xl md:text-4xl font-bold leading-tight max-w-xl mx-auto ${dark ? 'text-white' : 'text-green-900'}`}>
            Safe, Fresh &amp; Nutritious Agricultural Products
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title}
              className={`fade-in relative overflow-hidden border rounded-2xl p-8 transition-all duration-300 cursor-pointer card-top-bar
                ${dark
                  ? 'bg-green-900/20 border-green-400/10 hover:border-green-600/40 hover:shadow-xl hover:shadow-green-400/5'
                  : 'bg-white border-green-800/10 hover:border-green-600 hover:shadow-xl hover:-translate-y-1'}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5
                ${dark ? 'bg-green-400/8' : 'bg-green-50'}`}>
                <svg className={`w-6 h-6 ${dark ? 'fill-green-400' : 'fill-green-700'}`} viewBox="0 0 24 24">
                  {s.icon}
                </svg>
              </div>
              <h3 className={`font-serif text-xl font-bold mb-3 ${dark ? 'text-white' : 'text-green-900'}`}>{s.title}</h3>
              <p className={`text-sm leading-relaxed ${dark ? 'text-green-300/60' : 'text-green-700/70'}`}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
